import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {doc, getDoc} from 'firebase/firestore';
import {getDownloadURL, listAll, ref as storageRef} from 'firebase/storage';
import {FaArrowLeft, FaTimes} from 'react-icons/fa';
import {db, storage} from '../utils/firebase';
import {useLang} from '../context/LanguageContext';
import {DEFAULT_PREMIUM_TICKETS_ENABLED, PREMIUM_TICKETS_STORAGE_FOLDER} from '../utils/premiumTickets';
import Footer from '../components/Footer';
import Loading from '../components/Loading';

const PremiumTicketsPage = () => {
    const navigate = useNavigate();
    const {t} = useLang();

    const [enabled, setEnabled] = useState(DEFAULT_PREMIUM_TICKETS_ENABLED);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [images, setImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [error, setError] = useState('');
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const snap = await getDoc(doc(db, 'appSettings', 'premiumTickets'));
                if (snap.exists() && typeof snap.data().enabled === 'boolean') {
                    setEnabled(snap.data().enabled);
                }
            } catch (err) {
                // pastram valoarea default daca nu putem citi
            }
            setSettingsLoaded(true);
        };
        loadSettings();
    }, []);

    useEffect(() => {
        if (!settingsLoaded || !enabled) return;

        const loadImages = async () => {
            setLoadingImages(true);
            setError('');
            try {
                const folderRef = storageRef(storage, PREMIUM_TICKETS_STORAGE_FOLDER);
                const res = await listAll(folderRef);
                const items = await Promise.all(res.items.map(async (item) => ({
                    name: item.name,
                    url: await getDownloadURL(item),
                })));
                items.sort((a, b) => b.name.localeCompare(a.name));
                setImages(items);
            } catch (err) {
                console.error(err);
                setError(t('txt.premiumTicketsError'));
            }
            setLoadingImages(false);
        };
        loadImages();
    }, [settingsLoaded, enabled, t]);

    useEffect(() => {
        if (!previewImage) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setPreviewImage(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [previewImage]);

    return (
        <div className="bg-[#34495e] min-h-screen p-5 relative">
            <button
                onClick={() => navigate('/')}
                className="absolute top-5 left-5 px-4 py-2 bg-[#2ecc71] rounded flex items-center space-x-2"
            >
                <FaArrowLeft/>
                <span className="text-white">{t('btn.back')}</span>
            </button>

            <h1 className="text-xl text-white font-bold text-center mt-16 mb-6">
                {t('txt.premiumTicketsSectionTitle')}
            </h1>

            <div className="max-w-[900px] mx-auto">
                {!settingsLoaded && (
                    <div className="mt-8">
                        <Loading/>
                    </div>
                )}

                {settingsLoaded && !enabled && (
                    <p className="text-center text-yellow-300 text-sm">{t('txt.premiumTicketsDisabled')}</p>
                )}

                {settingsLoaded && enabled && (
                    <>
                        {loadingImages && (
                            <div className="mt-8">
                                <Loading/>
                            </div>
                        )}
                        {!loadingImages && error && (
                            <p className="text-center text-red-300 text-sm">{error}</p>
                        )}
                        {!loadingImages && !error && images.length === 0 && (
                            <p className="text-center text-gray-300 text-sm">{t('txt.premiumTicketsEmpty')}</p>
                        )}
                        {!loadingImages && !error && images.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {images.map(image => (
                                    <button
                                        key={image.name}
                                        type="button"
                                        onClick={() => setPreviewImage(image)}
                                        className="block bg-white rounded-lg shadow overflow-hidden"
                                    >
                                        <img src={image.url} alt={image.name} className="w-full h-40 object-cover"/>
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="mt-8">
                <Footer/>
            </div>

            {previewImage && (
                <div
                    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setPreviewImage(null)}
                >
                    <button
                        onClick={() => setPreviewImage(null)}
                        aria-label="Inchide"
                        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full text-xl"
                    >
                        <FaTimes/>
                    </button>
                    <img
                        src={previewImage.url}
                        alt={previewImage.name}
                        onClick={(e) => e.stopPropagation()}
                        className="max-w-full max-h-full object-contain rounded"
                    />
                </div>
            )}
        </div>
    );
};

export default PremiumTicketsPage;
